const toStringSafe = (value) => (typeof value === "string" ? value.trim() : "");
const toNumberSafe = (value, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return fallback;
};

const normalizeAddress = (address = {}) => ({
  name: toStringSafe(address.name),
  addressLine1: toStringSafe(address.addressLine1),
  addressLine2: toStringSafe(address.addressLine2),
  city: toStringSafe(address.city),
  country: toStringSafe(address.country),
  zipCode: toStringSafe(address.zipCode),
  phone: toStringSafe(address.phone),
});

const normalizeTracking = (tracking = {}) => ({
  number: toStringSafe(tracking.number),
  url: toStringSafe(tracking.url),
});

const normalizeShippingMethod = (method = {}) => ({
  type: toStringSafe(method.type),
  name: toStringSafe(method.name),
  fee: toNumberSafe(method.fee),
  estimatedDelivery: toStringSafe(method.estimatedDelivery),
  tracking: normalizeTracking(method.tracking),
});

const normalizeRefund = (refund) => {
  if (!refund || typeof refund !== "object") return null;

  return {
    status: toStringSafe(refund.status),
    amount: toNumberSafe(refund.amount),
    date: toStringSafe(refund.date),
    reason: toStringSafe(refund.reason),
    notes: toStringSafe(refund.notes),
    processedBy: toStringSafe(refund.processedBy),
  };
};

const normalizeProduct = (product = {}) => ({
  variantComboId: toStringSafe(product.variantComboId),
  productName: toStringSafe(product.productName),
  sku: toStringSafe(product.sku),
  quantity: toNumberSafe(product.quantity),
  unitPrice: toNumberSafe(product.unitPrice),
  total: toNumberSafe(product.total),
  image: toStringSafe(product.image),
});

export function normalizeOrder(order) {
  const normalizedProducts = Array.isArray(order.products)
    ? order.products.map(normalizeProduct)
    : [];

  const totalQuantity = normalizedProducts.reduce((acc, item) => acc + item.quantity, 0);

  const pricing = order.pricing || {};
  const paymentDetails = order.paymentDetails || {};

  const total = toNumberSafe(pricing.total);
  const amountPaid = toNumberSafe(paymentDetails.amountPaid, toNumberSafe(pricing.amountPaid));
  const balanceDue = Math.max(0, total - amountPaid);

  return {
    id: toStringSafe(order.id),
    orderNumber: toStringSafe(order.orderNumber),
    createdAt: toStringSafe(order.createdAt),
    orderDate: toStringSafe(order.orderDate),
    orderStatus: toStringSafe(order.orderStatus),
    fulfillmentStatus: toStringSafe(order.fulfillmentStatus),
    paymentStatus: toStringSafe(order.paymentStatus),
    salesChannel: toStringSafe(order.salesChannel),
    items: toNumberSafe(order.items, totalQuantity),
    customer: {
      id: toStringSafe(order.customer?.id),
      name: toStringSafe(order.customer?.name),
      email: toStringSafe(order.customer?.email),
      phone: toStringSafe(order.customer?.phone),
      billingAddress: toStringSafe(order.customer?.billingAddress),
    },
    billingAddress: normalizeAddress(order.billingAddress),
    shippingAddress: normalizeAddress(order.shippingAddress),
    shippingMethod: normalizeShippingMethod(order.shippingMethod),
    paymentDetails: {
      method: toStringSafe(paymentDetails.method),
      status: toStringSafe(paymentDetails.status),
      transactionId: toStringSafe(paymentDetails.transactionId),
      paymentDate: toStringSafe(paymentDetails.paymentDate),
      amountPaid,
      balanceDue,
    },
    pricing: {
      subTotal: toNumberSafe(pricing.subTotal),
      discounts: toNumberSafe(pricing.discounts),
      taxes: toNumberSafe(pricing.taxes),
      shipping: toNumberSafe(pricing.shipping),
      total,
      amountPaid: toNumberSafe(pricing.amountPaid, amountPaid),
    },
    refund: normalizeRefund(order.refund),
    products: normalizedProducts,
    notes: toStringSafe(order.notes),
    meta: {
      totalQuantity,
      productCount: normalizedProducts.length,
    },
  };
}

export function normalizeOrders(orders) {
  return Array.isArray(orders) ? orders.map(normalizeOrder) : [];
}
